import Container from '@cloudscape-design/components/container'
import Header from '@cloudscape-design/components/header'
import SpaceBetween from '@cloudscape-design/components/space-between'
import Box from '@cloudscape-design/components/box'
import Button from '@cloudscape-design/components/button'
import Cards from '@cloudscape-design/components/cards'
import ColumnLayout from '@cloudscape-design/components/column-layout'
import Link from '@cloudscape-design/components/link'
import Icon from '@cloudscape-design/components/icon'
import { useAppStore } from '@/store/app-store'
import type { NavPage } from '@/types'

interface FeatureCard {
  title: string
  description: string
  page: NavPage
  icon: string
}

const FEATURES: FeatureCard[] = [
  {
    title: 'Run Benchmarks',
    description: 'Run ad-hoc or scheduled latency benchmarks across Bedrock, Anthropic, and Grok. Measures TTFT, throughput, guardrail overhead, and network timing.',
    page: 'test',
    icon: 'caret-right-filled',
  },
  {
    title: 'Test Profiles',
    description: 'Configure which models to test. Choose from preset profiles (Bedrock cross-region, Anthropic, Grok) or create custom profiles with your own prompts.',
    page: 'profiles',
    icon: 'settings',
  },
  {
    title: 'Results & Trends',
    description: 'View run history, detailed breakdowns per profile, trend charts over time, and aggregated metrics. Filter by model, mode, and time range.',
    page: 'results',
    icon: 'status-positive',
  },
  {
    title: 'Model Lifecycle',
    description: 'Track Bedrock model end-of-life dates, legacy dates, and region availability. Get warned when using models approaching EOL.',
    page: 'lifecycle',
    icon: 'status-warning',
  },
  {
    title: 'Service Health',
    description: 'Monitor Anthropic and Grok service health with incident history from status.claude.com and status.x.ai.',
    page: 'anthropic-health',
    icon: 'status-info',
  },
  {
    title: 'Settings',
    description: 'Configure execution mode (Cloud/Local), AWS credentials, API keys, and latency simulation for testing.',
    page: 'settings',
    icon: 'edit',
  },
]

export function OverviewPage() {
  const setPage = useAppStore((s) => s.setPage)
  const executionMode = useAppStore((s) => s.executionMode)
  const isAuthenticated = useAppStore((s) => s.isAuthenticated)
  const credentialStatus = useAppStore((s) => s.credentialStatus)

  return (
    <SpaceBetween size="l">
      {/* Hero */}
      <Container
        header={
          <Header variant="h1" description="Measure, compare, and understand streaming latency across Amazon Bedrock, Anthropic, and Grok (xAI).">
            Bedrock Latency Diagnostics
          </Header>
        }
      >
        <ColumnLayout columns={3} variant="text-grid">
          <div>
            <Box variant="awsui-key-label">Mode</Box>
            <Box variant="awsui-value-large">{executionMode === 'cloud' ? '☁️ Cloud' : '💻 Local'}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Auth</Box>
            <Box variant="awsui-value-large">
              {executionMode === 'cloud'
                ? (isAuthenticated ? '✓ Signed in' : '— Not signed in')
                : (credentialStatus === 'verified' ? '✓ Credentials verified' : '— No credentials')}
            </Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Quick Action</Box>
            <Button variant="primary" onClick={() => setPage('test')}>Run a Benchmark</Button>
          </div>
        </ColumnLayout>
      </Container>

      {/* Quick Start */}
      <Container header={<Header variant="h2">Quick Start</Header>}>
        <SpaceBetween size="s">
          <Box variant="p">
            <strong>1.</strong> Go to{' '}
            <Link onFollow={(e) => { e.preventDefault(); setPage('profiles') }} href="/profiles">Profiles</Link>
            {' '}— pick models and enable them
          </Box>
          <Box variant="p">
            <strong>2.</strong> Go to{' '}
            <Link onFollow={(e) => { e.preventDefault(); setPage('test') }} href="/test">Test</Link>
            {' '}— set iterations and click Run
          </Box>
          <Box variant="p">
            <strong>3.</strong> View{' '}
            <Link onFollow={(e) => { e.preventDefault(); setPage('results') }} href="/results">Results</Link>
            {' '}— compare TTFT, throughput, and network overhead
          </Box>
        </SpaceBetween>
      </Container>

      {/* Feature Cards */}
      <Cards
        cardDefinition={{
          header: (item) => (
            <Link
              fontSize="heading-m"
              onFollow={(e) => { e.preventDefault(); setPage(item.page) }}
              href={`/${item.page}`}
            >
              <Icon name={item.icon as any} /> {item.title}
            </Link>
          ),
          sections: [
            {
              id: 'description',
              content: (item) => item.description,
            },
          ],
        }}
        cardsPerRow={[{ cards: 1 }, { minWidth: 400, cards: 2 }, { minWidth: 700, cards: 3 }]}
        items={FEATURES}
        header={<Header variant="h2">Features</Header>}
      />

      {/* Providers */}
      <Container header={<Header variant="h2">Supported Providers</Header>}>
        <ColumnLayout columns={3} variant="text-grid">
          <div>
            <Box variant="awsui-key-label">Amazon Bedrock</Box>
            <Box variant="p" color="text-body-secondary" fontSize="body-s">
              InvokeModelStream, ConverseStream, Guardrails, Cross-region inference (US/EU/APAC/Global)
            </Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Anthropic</Box>
            <Box variant="p" color="text-body-secondary" fontSize="body-s">
              SSE streaming via api.anthropic.com with API key authentication
            </Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Grok (xAI)</Box>
            <Box variant="p" color="text-body-secondary" fontSize="body-s">
              OpenAI-compatible streaming via api.x.ai with cached token tracking
            </Box>
          </div>
        </ColumnLayout>
      </Container>

      {/* Metrics */}
      <Container header={<Header variant="h2">What We Measure</Header>}>
        <ColumnLayout columns={2}>
          <SpaceBetween size="xs">
            <Box variant="p"><strong>TTFT</strong> — Time to first token (client-measured, full network path)</Box>
            <Box variant="p"><strong>BR TTFT</strong> — Bedrock server-side first byte latency</Box>
            <Box variant="p"><strong>BR Invocation</strong> — Bedrock total invocation time</Box>
            <Box variant="p"><strong>Guardrails</strong> — Input and output guardrail processing time</Box>
            <Box variant="p"><strong>Throughput</strong> — Output tokens per second during streaming</Box>
          </SpaceBetween>
          <SpaceBetween size="xs">
            <Box variant="p"><strong>Network Overhead</strong> — Gap between client and server-reported time</Box>
            <Box variant="p"><strong>DNS / TCP / TLS</strong> — Connection setup timing per endpoint</Box>
            <Box variant="p"><strong>E2E Total</strong> — Full round-trip including all hops</Box>
            <Box variant="p"><strong>CRIS / Region</strong> — Cross-region inference profile detection</Box>
            <Box variant="p"><strong>Cached Tokens</strong> — Prompt caching metrics (Bedrock, Grok)</Box>
          </SpaceBetween>
        </ColumnLayout>
      </Container>
    </SpaceBetween>
  )
}
